import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardBody, CardHeader, Row, Col, CardTitle, Collapse, Button } from "reactstrap";
import NotesTab from "views/inventory/tabs/NotesTab";
import {
  formatDateForDisplay,
  formatDateTimeForDisplay,
} from "../../../utils/dateUtils";
import { formatPriceWithDollar, toNumber } from "../../../utils/currency";
import {
  getRemainingLateFeeDue,
  getSchedulePaymentAmount,
  getScheduleTotalDue,
} from "../../../utils/paymentSchedule";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import ReceiptDrawer from "../components/ReceiptDrawer";

/** Compact datetime for narrow snapshot table columns (e.g. 7/22/2026 7:24 AM) */
const formatCompactDateTime = (dateValue) => {
  if (!dateValue) return "-";
  try {
    const date =
      dateValue instanceof Date
        ? dateValue
        : typeof dateValue === "object" && typeof dateValue.toDate === "function"
          ? dateValue.toDate()
          : new Date(dateValue);
    if (isNaN(date.getTime())) return "-";
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()} ${hours}:${minutes} ${ampm}`;
  } catch (e) {
    return "-";
  }
};

const StackedHeader = ({ lines }) => (
  <>
    {lines.map((line, i) => (
      <div key={i}>{line}</div>
    ))}
  </>
);

const getChargeOffBalanceTable = (data) => {
  const balances = data?.balances || data?.current_bal || {};
  const columns = [
    {
      id: "original",
      label: "Original CO",
      balance: balances.charge_off_original_balance,
      tax: balances.charge_off_original_tax,
      total: balances.charge_off_original_total,
    },
    {
      id: "adjusted-acv",
      label: "Adjusted-ACV",
      balance: balances.charge_off_adjusted_acv_balance,
      tax: balances.charge_off_adjusted_acv_tax,
      total: balances.charge_off_adjusted_acv_total,
    },
    {
      id: "adjusted-other",
      label: "Adjusted-Other",
      balance: balances.charge_off_adjusted_other_balance,
      tax: balances.charge_off_adjusted_other_tax,
      total: balances.charge_off_adjusted_other_total,
    },
    {
      id: "recoveries",
      label: "Recoveries",
      balance: balances.charge_off_recoveries_balance,
      tax: balances.charge_off_recoveries_tax,
      total: balances.charge_off_recoveries_total,
    },
    {
      id: "current",
      label: "Current CO",
      balance: balances.charge_off_current_balance,
      tax: balances.charge_off_current_tax,
      total: balances.charge_off_current_total,
    },
  ];
  const metrics = [
    { id: "balance", labelKey: "chargeOffBalanceCol" },
    { id: "tax", labelKey: "chargeOffTaxCol" },
    { id: "total", labelKey: "chargeOffTotalCol", isTotal: true },
  ];
  return { columns, metrics };
};

const normalizeRows = (data) => {
  const rows = Array.isArray(data?.remaining_payments)
    ? data.remaining_payments
    : Array.isArray(data?.remainingPayments)
      ? data.remainingPayments
      : Array.isArray(data?.payments)
        ? data.payments
        : [];

  return rows.map((item, idx) => {
    const dueDate = item.date || null;
    const description = item.note || "";
    const daysPastDue = item.day_past_due ?? item.day_past_due ?? "";
    const pmtAmount = getSchedulePaymentAmount(item);
    const lateFee = getRemainingLateFeeDue(item);
    const totalDue = getScheduleTotalDue(item);

    return {
      id: item.id || item.documentId || idx,
      dueDate: formatDateForDisplay(dueDate),
      description,
      daysPastDue,
      pmtAmount: formatPriceWithDollar(pmtAmount),
      lateFee: formatPriceWithDollar(lateFee),
      totalDue: formatPriceWithDollar(totalDue),
    };
  });
};

// Try to normalize bucketed payment performance counts for the summary bar
const normalizePerformanceBuckets = (data) => {
  const payments = Array.isArray(data?.payments) ? data.payments : [];

  const stats = {
    all: payments.length,
    current: 0,
    late0_15: 0,
    late16_30: 0,
    late31_60: 0,
    late61_90: 0,
    gte90: 0,
    gte120: 0,
    extd: 0
  };

  payments.forEach(payment => {
    const lateDays = payment.late_day || 0;

    if (lateDays <= 0) stats.current++;
    else if (lateDays <= 15) stats.late0_15++;
    else if (lateDays <= 30) stats.late16_30++;
    else if (lateDays <= 60) stats.late31_60++;
    else if (lateDays <= 90) stats.late61_90++;
    else if (lateDays < 120) stats.gte90++;
    else stats.gte120++;
  });

  return stats;
};

const normalizeTransactions = (data) => {
  const list = Array.isArray(data?.transactions)
    ? data.transactions
    : Array.isArray(data?.payments)
      ? data.payments
      : Array.isArray(data)
        ? data
        : [];

  return list
    ?.filter((transaction) => transaction?.payment_type !== "late_fee_assessed")
    .map((transaction, idx) => ({
    id: transaction?.id || transaction?.documentId || idx + 1,
    date: transaction?.date || transaction?.createdAt || "-",
    amount: transaction?.amount ?? transaction?.payment_amount ?? transaction?.pmt_amount ?? 0,
    collected:
      transaction?.collected ??
      transaction?.received_amount ??
      transaction?.amount ??
      0,
    adjusted: transaction?.adjusted ?? transaction?.adjustment ?? 0,
    posted_by:
      transaction?.created_user?.user_detail?.full_name ||
      transaction?.created_user?.user_detail?.name ||
      "-",
    beginningBalance:
      transaction?.beg_balance ??
      transaction?.beginning_balance ??
      transaction?.starting_balance ??
      0,
    endingBalance:
      transaction?.end_balance ??
      transaction?.ending_balance ??
      transaction?.ending_balance_amount ??
      0,
    paymentForm: transaction?.payment_form || transaction?.paymentMethod || "-",
    refNumber:
      transaction?.ref_number || transaction?.refNumber || transaction?.reference || "-",
    createdUser: transaction?.created_user || null,
    actionType: transaction?.action_type || transaction?.actionType || "-",
    deliveryMethod:
      transaction?.delivery_method || transaction?.deliveryMethod || "-",
    note: transaction?.note || transaction?.comments || transaction?.description || "-",
    description: transaction?.description || transaction?.comments || transaction?.note || "",
    payment_type: transaction?.payment_type || "",
    receipt_id: transaction?.receipt_id || transaction?.receiptId || "",
    tax_amount: transaction?.tax_amount || 0,
    late_fees_amount: transaction?.late_fees_amount || 0,
    sales_tax_beg_bal: transaction?.sales_tax_beg_bal || 0,
    sales_tax_end_bal: transaction?.sales_tax_end_bal || 0,
    effect_date: transaction?.effect_date || transaction?.createdAt || "-",
    cpi_amount: transaction?.cpi_amount || 0,
    cpi_beg_bal: transaction?.cpi_beg_bal || 0,
    cpi_end_bal: transaction?.cpi_end_bal || 0,
    // def_down specific fields
    def_down_beg_bal: transaction?.def_down_beg_bal ?? 0,
    def_down_end_bal: transaction?.def_down_end_bal ?? 0,
    def_down_collected: transaction?.def_down_collected ?? 0,
    // prin specific fields
    beg_balance: transaction?.beg_balance ?? 0,
    end_balance: transaction?.end_balance ?? 0,
    misc_fee_beg_bal: transaction?.misc_fee_beg_bal ?? 0,
    misc_fee_end_bal: transaction?.misc_fee_end_bal ?? 0,
    misc_fee_collected: transaction?.misc_fee_collected ?? 0,
    misc_fee_adjusted: transaction?.misc_fee_adjusted ?? 0,
    late_fee_beg_bal: transaction?.late_fee_beg_bal ?? 0,
    late_fee_end_bal: transaction?.late_fee_end_bal ?? 0,
    late_fee_collected: transaction?.late_fee_collected ?? 0,
    late_fee_adjusted: transaction?.late_fee_adjusted ?? 0,
    // detail array for receipt breakdown
    detail: Array.isArray(transaction?.detail) ? transaction.detail : null,
  }));
};

// Notes summary counters shown above notes list on the right side
const normalizeNotesBuckets = (data) => {
  const summary = data?.notes_summary || data?.notesSummary || data?.communications || data?.calls || {};

  const co = summary.calls_out ?? summary.callsOut ?? summary.outbound ?? 0;
  const ci = summary.calls_in ?? summary.callsIn ?? summary.inbound ?? 0;
  const collector = summary.notes ?? summary.collections ?? 0;
  const system = summary.main ?? summary.auto ?? summary.automated ?? 0;
  const ip = summary.ip ?? summary.internet ?? 0;
  const ptp = summary.ptp ?? summary.promise_to_pay ?? summary.promiseToPay ?? 0;

  const toNumberSafe = (v) => (typeof v === 'number' ? v : (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v)) ? Number(v) : 0));

  return {
    callsOut: toNumberSafe(co),
    callsIn: toNumberSafe(ci),
    collector: toNumberSafe(collector),
    system: toNumberSafe(system),
    ip: toNumberSafe(ip),
    ptp: toNumberSafe(ptp),
  };
};

const canViewTransactions = (permission) =>
  permission?.can_view_transactions === true || permission?.can_veiw_transactions === true;

const SnapshotTab = ({
  data,
  activeColor = "secondary",
  openDrawer,
  accountData,
  permission,
  isTheme2 = false,
  id: accountId,
}) => {
  const { t } = useTranslation();
  const showTransactionHistory = canViewTransactions(permission);
  const isChargeOff = data?.act_status === "charge_off";
  const rows = useMemo(() => normalizeRows(data), [data]);
  const chargeOffTable = useMemo(() => getChargeOffBalanceTable(data), [data]);
  const performanceStats = useMemo(() => normalizePerformanceBuckets(data), [data]);
  const transactionRows = useMemo(() => normalizeTransactions(data), [data]);

  const handleOpenEmail = (row) => {
    openDrawer("send", {
      transaction_id: row.id,
      transaction: row,
    });
  };

  const notesBuckets = useMemo(() => normalizeNotesBuckets(data), [data]);
  const [selectedNoteBucket, setSelectedNoteBucket] = useState(null);
  const [isPerformanceDetailsOpen, setIsPerformanceDetailsOpen] = useState(false);
  const [selectedPerformanceFilter, setSelectedPerformanceFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedPerformanceRows, setExpandedPerformanceRows] = useState({});
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const darkMode = localStorage.getItem("darkMode");
    return darkMode ? JSON.parse(darkMode) : true;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const darkMode = localStorage.getItem("darkMode");
      const newTheme = darkMode ? JSON.parse(darkMode) : true;
      setIsDarkMode((prev) => (prev !== newTheme ? newTheme : prev));
    }, 800);
    return () => clearInterval(interval);
  }, []);

  const txExpandedStyles = useMemo(
    () => ({
      panel: {
        backgroundColor: isDarkMode ? "#27293d" : "#fafafa",
        borderTop: isDarkMode ? "1px solid rgba(255, 255, 255, 0.12)" : undefined,
      },
      table: {
        fontSize: "0.75rem",
        color: isDarkMode ? "rgba(255, 255, 255, 0.9)" : undefined,
      },
      headerMuted: {
        fontWeight: "600",
        color: isDarkMode ? "rgba(255, 255, 255, 0.65)" : undefined,
      },
      collected: {
        fontWeight: "600",
        color: isDarkMode ? "#6eb5ff" : "#007bff",
      },
      mutedNote: {
        fontSize: "0.75rem",
        alignSelf: "center",
        color: isDarkMode ? "rgba(255, 255, 255, 0.55)" : undefined,
      },
      rowText: isDarkMode ? "#ffffff" : "#000000",
      rowSubText: isDarkMode ? "rgba(255, 255, 255, 0.65)" : "#666",
      collectedCell: isDarkMode ? "#6eb5ff" : "#007bff",
    }),
    [isDarkMode]
  );

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const togglePerformanceExpand = (id) => {
    setExpandedPerformanceRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderExpandedContent = (row) => {
    const isDefDown =
      row.payment_type === "def_down" ||
      row.payment_type === "def_down_reversal" ||
      row.payment_type === "reversal";
    const hasDefDownBalances =
      toNumber(row.def_down_beg_bal) !== 0 ||
      toNumber(row.def_down_collected) !== 0 ||
      toNumber(row.def_down_end_bal) !== 0;
    const showDefDownSnapshot = isDefDown || hasDefDownBalances;
    const isReversal = row.payment_type === "reversal" || row.payment_type === "def_down_reversal";

    return (
      <tr>
        <td colSpan="8" className="p-3" style={txExpandedStyles.panel}>
          <Row>
            <Col md="12">
              <table className="table table-sm table-borderless mb-0" style={txExpandedStyles.table}>
                <thead>
                  <tr>
                    <th className="py-0 border-0"></th>
                    <th className="py-0 border-0" style={txExpandedStyles.headerMuted}>Beg Bal</th>
                    <th className="py-0 border-0" style={txExpandedStyles.collected}>Collected</th>
                    <th className="py-0 border-0" style={txExpandedStyles.headerMuted}>Adjusted</th>
                    <th className="py-0 border-0" style={txExpandedStyles.headerMuted}>Rebated</th>
                    <th className="py-0 border-0" style={txExpandedStyles.headerMuted}>End Bal</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-right py-0 border-0 font-weight-bold">Down Payment:</td>
                    <td className="py-0 border-0">{showDefDownSnapshot ? formatPriceWithDollar(row.def_down_beg_bal) : "$0.00"}</td>
                    <td className="py-0 border-0" style={txExpandedStyles.collected}>
                      {showDefDownSnapshot && toNumber(row.def_down_collected) !== 0
                        ? isReversal
                          ? formatPriceWithDollar(row.def_down_collected)
                          : `(${formatPriceWithDollar(row.def_down_collected)})`
                        : "$0.00"}
                    </td>
                    <td className="py-0 border-0">$0.00</td>
                    <td className="py-0 border-0"></td>
                    <td className="py-0 border-0">{showDefDownSnapshot ? formatPriceWithDollar(row.def_down_end_bal) : "$0.00"}</td>
                  </tr>
                  <tr>
                    <td className="text-right py-0 border-0 font-weight-bold">Prin:</td>
                    <td className="py-0 border-0">{formatPriceWithDollar(row.beg_balance)}</td>
                    <td className="py-0 border-0" style={txExpandedStyles.collected}>
                      {row.collected !== 0 ? (isReversal ? formatPriceWithDollar(row.collected) : `(${formatPriceWithDollar(row.collected)})`) : "$0.00"}
                    </td>
                    <td className="py-0 border-0">$0.00</td>
                    <td className="py-0 border-0"></td>
                    <td className="py-0 border-0">{formatPriceWithDollar(row.end_balance)}</td>
                  </tr>
                  <tr>
                    <td className="text-right py-0 border-0 font-weight-bold">Sales Tax:</td>
                    <td className="py-0 border-0">{formatPriceWithDollar(row.sales_tax_beg_bal || 0)}</td>
                    <td className="py-0 border-0" style={txExpandedStyles.collected}>
                      {row.tax_amount && row.tax_amount !== 0 ? (isReversal ? formatPriceWithDollar(row.tax_amount) : `(${formatPriceWithDollar(row.tax_amount)})`) : "$0.00"}
                    </td>
                    <td className="py-0 border-0">$0.00</td>
                    <td className="py-0 border-0"></td>
                    <td className="py-0 border-0">{formatPriceWithDollar(row.sales_tax_end_bal || 0)}</td>
                  </tr>
                  <tr>
                    <td className="text-right py-0 border-0 font-weight-bold">Interest:</td>
                    <td className="py-0 border-0">$0.00</td>
                    <td className="py-0 border-0" style={txExpandedStyles.collected}>$0.00</td>
                    <td className="py-0 border-0">$0.00</td>
                    <td className="py-0 border-0">$0.00</td>
                    <td className="py-0 border-0">$0.00</td>
                  </tr>
                  <tr>
                    <td className="text-right py-0 border-0 font-weight-bold">Late Fees:</td>
                    <td className="py-0 border-0">$0.00</td>
                    <td className="py-0 border-0" style={txExpandedStyles.collected}>
                      {row.late_fees_amount && row.late_fees_amount !== 0 ? `(${formatPriceWithDollar(row.late_fees_amount)})` : "$0.00"}
                    </td>
                    <td className="py-0 border-0">$0.00</td>
                    <td className="py-0 border-0"></td>
                    <td className="py-0 border-0">$0.00</td>
                  </tr>
                  <tr>
                    <td className="text-right py-0 border-0 font-weight-bold">Misc Fees:</td>
                    <td className="py-0 border-0">{formatPriceWithDollar(row.misc_fee_beg_bal)}</td>
                    <td className="py-0 border-0" style={txExpandedStyles.collected}>{formatPriceWithDollar(row.misc_fee_collected)}</td>
                    <td className="py-0 border-0">{formatPriceWithDollar(row.misc_fee_adjusted)}</td>
                    <td className="py-0 border-0"></td>
                    <td className="py-0 border-0">{formatPriceWithDollar(row.misc_fee_end_bal)}</td>
                  </tr>
                </tbody>
              </table>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md="12" className="d-flex align-items-center justify-content-start">
              <div className="d-flex" style={{ gap: '10px' }}>
                <Button outline color="info" size="sm" style={{ fontWeight: 'bold', borderWidth: '1px' }} onClick={() => {
                  setSelectedTransaction(row);
                  setIsReceiptOpen(true);
                }}>
                  {t('transactions.receipt')}
                </Button>
                {accountData?.customer?.email ? (
                  <Button outline color="info" size="sm" style={{ fontWeight: 'bold', borderWidth: '1px' }} onClick={() => handleOpenEmail(row)}>
                    {t('transactions.emailReceipt')}
                  </Button>
                ) : (
                  <span className="text-muted" style={txExpandedStyles.mutedNote}>
                    {t('transactions.missingEmail')}
                  </span>
                )}
              </div>
            </Col>
          </Row>
        </td>
      </tr>
    );
  };



  const filteredPayments = useMemo(() => {
    if (!data?.payments) return [];
    if (selectedPerformanceFilter === 'all') return data.payments;

    return data.payments.filter(payment => {
      const lateDay = payment.late_day || 0;
      if (selectedPerformanceFilter === 'current') return lateDay <= 0;
      if (selectedPerformanceFilter === 'late0_15') return lateDay > 0 && lateDay <= 15;
      if (selectedPerformanceFilter === 'late16_30') return lateDay > 15 && lateDay <= 30;
      if (selectedPerformanceFilter === 'late31_60') return lateDay > 30 && lateDay <= 60;
      if (selectedPerformanceFilter === 'late61_90') return lateDay > 60 && lateDay <= 90;
      if (selectedPerformanceFilter === 'gte90') return lateDay > 90 && lateDay < 120;
      if (selectedPerformanceFilter === 'gte120') return lateDay >= 120;
      if (selectedPerformanceFilter === 'extd') return false; // Placeholder for now
      return false;
    });
  }, [data, selectedPerformanceFilter]);
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const performanceColumns = useMemo(() => [
    { Header: t('snapshotTab.metric'), accessor: "label" },
    { Header: t('snapshotTab.value'), accessor: "value" },
  ], [t]);

  const transactionColumns = useMemo(
    () => [
      {
        Header: t("transactions.postedBy"),
        accessor: "posted_by",
        Cell: ({ value }) => value || "-",
        style: { fontSize: "12px" },
        width: 150,
      },
      {
        Header: t("transactions.postDateEffectDate"),
        accessor: "date",
        Cell: ({ value }) => {
          const postDate = formatDateTimeForDisplay(value);
          const effectDate = formatDateTimeForDisplay(value);
          return (
            <div>
              <div style={{ fontSize: "12px" }}>{postDate}</div>
              <div style={{ fontSize: "11px", color: "#666" }}>
                {effectDate}
              </div>
            </div>
          );
        },
        style: { fontSize: "12px" },
        width: 150,
      },
      {
        Header: t("transactions.begAcctBalance"),
        accessor: "beginningBalance",
        Cell: ({ value }) => formatPriceWithDollar(value),
        style: { fontSize: "12px" },
        width: 120,
      },
      {
        Header: t("transactions.collected"),
        accessor: "collected",
        Cell: ({ row, value }) => {
          const isReversal = row.original.payment_type === "reversal" || row.original.payment_type === "def_down_reversal";
          const formatted = formatPriceWithDollar(value);
          if (isReversal) return formatted;
          return value < 0
            ? `(${formatted.replace("$", "").replace(",", "")})`
            : formatted;
        },
        width: 120,
      },
      {
        Header: t("transactions.adjusted"),
        accessor: "adjusted",
        Cell: ({ value }) => formatPriceWithDollar(value || 0),
        width: 120,
      },
      {
        Header: t("transactions.endAcctBalance"),
        accessor: "endingBalance",
        Cell: ({ value }) => formatPriceWithDollar(value),
        width: 120,
      },
    ],
    [t]
  );

  return (
    <div className="container-fluid px-0">
      <Row>
        <Col md="6">
          {isChargeOff && (
            <Card>
              <CardHeader>
                <CardTitle className="mb-0" style={{ fontWeight: "bold" }}>
                  {t('snapshotTab.chargeOffBalance')}
                </CardTitle>
              </CardHeader>
              <CardBody className="pt-2">
                <div className="table-responsive">
                  <table
                    className="table table-sm mb-0"
                    style={{
                      borderCollapse: "separate",
                      borderSpacing: 0,
                      minWidth: "100%",
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            backgroundColor: activeColorStyle.borderColor,
                            color: "#fff",
                            fontWeight: 600,
                            padding: "5px 10px",
                            position: "sticky",
                            textTransform: "math-auto",
                            top: 0,
                            zIndex: 1,
                          }}
                        />
                        {chargeOffTable.columns.map((column) => (
                          <th
                            key={column.id}
                            style={{
                              backgroundColor: activeColorStyle.borderColor,
                              color: "#fff",
                              fontWeight: 600,
                              padding: "5px 10px",
                              position: "sticky",
                              textTransform: "math-auto",
                              top: 0,
                              zIndex: 1,
                              textAlign: "right",
                            }}
                          >
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {chargeOffTable.metrics.map((metric) => (
                        <tr key={metric.id} style={{ fontWeight: metric.isTotal ? 700 : 400 }}>
                          <td style={{ fontSize: 11, padding: "5px 10px" }}>
                            {t(`snapshotTab.${metric.labelKey}`)}
                          </td>
                          {chargeOffTable.columns.map((column) => (
                            <td
                              key={column.id}
                              style={{ fontSize: 11, padding: "5px 10px", textAlign: "right" }}
                            >
                              {formatPriceWithDollar(column[metric.id])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          )}

          {!isChargeOff && (
          <Card>
            <CardHeader>
              <CardTitle className="mb-0" style={{ fontWeight: "bold" }}>
                {t('snapshotTab.remainingPayments')}
              </CardTitle>
            </CardHeader>
            <CardBody className="pt-2" style={{ maxHeight: 300, overflowY: "auto" }}>
              {rows.length === 0 ? (
                <div className="text-center text-muted py-5">
                  {t('snapshotTab.noRemainingPayments')}
                </div>
              ) : (
                <div className="table-responsive">
                  <table
                    className="table table-sm mb-0"
                    style={{
                      borderCollapse: "separate",
                      borderSpacing: 0,
                      minWidth: "100%",
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            backgroundColor: activeColorStyle.borderColor,
                            color: "#fff",
                            fontWeight: 600,
                            padding: "5px 10px",
                            position: "sticky",
                            textTransform: "math-auto",
                            top: 0,
                            zIndex: 1,
                          }}
                        >
                          {t('snapshotTab.dueDate')}
                        </th>
                        <th
                          style={{
                            backgroundColor: activeColorStyle.borderColor,
                            color: "#fff",
                            fontWeight: 600,
                            padding: "5px 10px",
                            position: "sticky",
                            textTransform: "math-auto",
                            top: 0,
                            zIndex: 1,
                          }}
                        >
                          {t('snapshotTab.description')}
                        </th>
                        <th
                          style={{
                            backgroundColor: activeColorStyle.borderColor,
                            color: "#fff",
                            fontWeight: 600,
                            padding: "5px 10px",
                            textAlign: "center",
                            position: "sticky",
                            textTransform: "math-auto",
                            top: 0,
                            zIndex: 1,
                          }}
                        >
                          {t('snapshotTab.daysPastDue')}
                        </th>
                        <th
                          style={{
                            backgroundColor: activeColorStyle.borderColor,
                            color: "#fff",
                            fontWeight: 600,
                            padding: "5px 10px",
                            textAlign: "center",
                            position: "sticky",
                            textTransform: "math-auto",
                            top: 0,
                            zIndex: 1,
                          }}
                        >
                          {t('snapshotTab.pmtAmount')}
                        </th>
                        <th
                          style={{
                            backgroundColor: activeColorStyle.borderColor,
                            color: "#fff",
                            fontWeight: 600,
                            padding: "5px 10px",
                            textAlign: "center",
                            position: "sticky",
                            textTransform: "math-auto",
                            top: 0,
                            zIndex: 1,
                          }}
                        >
                          {t('snapshotTab.lateFee')}
                        </th>
                        <th
                          style={{
                            backgroundColor: activeColorStyle.borderColor,
                            color: "#fff",
                            fontWeight: 600,
                            padding: "5px 10px",
                            textAlign: "center",
                            textTransform: "math-auto",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                          }}
                        >
                          {t('snapshotTab.totalDue')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const lateDay = Number(row.daysPastDue) || 0;
                        let rowBgColor = '#28a745';
                        if (lateDay > 0) rowBgColor = '#f6ad55';
                        if (lateDay > 15) rowBgColor = '#f6a04d';
                        if (lateDay > 30) rowBgColor = '#d97706';
                        if (lateDay > 60) rowBgColor = '#bf6d00';
                        if (lateDay >= 90) rowBgColor = '#d66d75';
                        if (lateDay >= 120) rowBgColor = '#ef5350';

                        return (
                          <tr key={row.id}>
                            <td
                              style={{
                                backgroundColor: rowBgColor,
                                fontSize: 11,
                                padding: "5px 10px",
                              }}
                            >
                              <span style={{
                                color: "#fff",
                                fontWeight: 600,
                              }}>
                                {row.dueDate || "-"}
                              </span>
                            </td>
                            <td
                              style={{
                                backgroundColor: rowBgColor,
                                color: "#fff",
                                fontSize: 11,
                                padding: "5px 10px",
                              }}
                            >
                              <span style={{
                                color: "#fff",
                                fontWeight: 600,
                              }}>
                                {row.description || "-"}
                              </span>
                            </td>
                            <td
                              style={{
                                backgroundColor: rowBgColor,
                                color: "#fff",
                                textAlign: "center",
                                fontSize: 11,
                                padding: "5px 10px",
                              }}
                            >
                              <span style={{
                                color: "#fff",
                                fontWeight: 600,
                              }}>
                                {row.daysPastDue ?? "-"}
                              </span>
                            </td>
                            <td
                              style={{
                                backgroundColor: rowBgColor,
                                color: "#fff",
                                textAlign: "center",
                                fontSize: 11,
                                padding: "5px 10px",
                              }}
                            >
                              <span style={{
                                color: "#fff",
                                fontWeight: 600,
                              }}>
                                {row.pmtAmount || "-"}
                              </span>
                            </td>
                            <td
                              style={{
                                backgroundColor: rowBgColor,
                                color: "#fff",
                                textAlign: "center",
                                fontSize: 11,
                                padding: "5px 10px",
                              }}
                            >
                              <span style={{
                                color: "#fff",
                                fontWeight: 600,
                              }}>
                                {row.lateFee || "-"}
                              </span>
                            </td>
                            <td
                              style={{
                                backgroundColor: rowBgColor,
                                color: "#fff",
                                textAlign: "center",
                                fontSize: 11,
                                padding: "5px 10px",
                              }}
                            >
                              <span style={{
                                color: "#fff",
                                fontWeight: 600,
                              }}>
                                {row.totalDue || "-"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
          )}

          <Card className="mt-2">

            <CardHeader>
              <CardTitle className="mb-0" style={{ fontWeight: "bold" }}>{t('snapshotTab.paymentPerformance')}</CardTitle>
            </CardHeader>
            <CardBody className="pt-2">
              {/* Summary buckets bar - ALWAYS VISIBLE */}
              <div className="mb-2">
                <div style={{ display: "grid", gridTemplateColumns: "110px repeat(7, 1fr) 110px", gap: 8 }}>
                  <div className="text-center text-muted" style={{ fontSize: 12 }}>{t('snapshotTab.all')}</div>
                  <div className="text-center text-muted" style={{ fontSize: 12 }}>{t('snapshotTab.current')}</div>
                  <div className="text-center text-muted" style={{ fontSize: 12 }}>{t('snapshotTab.late0_15')}</div>
                  <div className="text-center text-muted" style={{ fontSize: 12 }}>{t('snapshotTab.late16_30')}</div>
                  <div className="text-center text-muted" style={{ fontSize: 12 }}>{t('snapshotTab.late31_60')}</div>
                  <div className="text-center text-muted" style={{ fontSize: 12 }}>{t('snapshotTab.late61_90')}</div>
                  <div className="text-center text-muted" style={{ fontSize: 12 }}>{t('snapshotTab.gte90Late')}</div>
                  <div className="text-center text-muted" style={{ fontSize: 12 }}>{t('snapshotTab.gte120Late')}</div>
                  <div className="text-center text-muted" style={{ fontSize: 12 }}>{t('snapshotTab.extd')}</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "110px repeat(7, 1fr) 110px", gap: 0 }}>
                  <div className="text-center"
                    onClick={() => setSelectedPerformanceFilter('all')}
                    style={{ background: isDarkMode ? "#27293d" : "#f1f3f5", color: isDarkMode ? "white" : "#333", height: "30px", display: "flex", justifyContent: "center", alignItems: "center", fontSize: 12, cursor: 'pointer', border: selectedPerformanceFilter === 'all' ? `2px solid ${activeColorStyle.borderColor}` : '1px solid rgba(255,255,255,0.1)' }}
                  >
                    {performanceStats.all || 0}
                  </div>
                  <div className="text-center"
                    onClick={() => setSelectedPerformanceFilter('current')}
                    style={{ background: "linear-gradient(to bottom left, #2ecc71, #27ae60)", color: "white", height: "30px", justifyContent: "center", display: "flex", alignItems: "center", fontSize: 12, cursor: 'pointer', border: selectedPerformanceFilter === 'current' ? `2px solid ${isDarkMode ? 'white' : 'black'}` : 'none' }}
                  >
                    {performanceStats.current || 0}
                  </div>
                  <div className="text-center"
                    onClick={() => setSelectedPerformanceFilter('late0_15')}
                    style={{ background: "linear-gradient(to bottom left, #f6ad55, #ed8936)", color: "white", height: "30px", display: "flex", justifyContent: "center", alignItems: "center", fontSize: 12, cursor: 'pointer', border: selectedPerformanceFilter === 'late0_15' ? `2px solid ${isDarkMode ? 'white' : 'black'}` : 'none' }}
                  >
                    {performanceStats.late0_15 || 0}
                  </div>
                  <div className="text-center"
                    onClick={() => setSelectedPerformanceFilter('late16_30')}
                    style={{ background: "linear-gradient(to bottom left, #f6a04d, #e07a2a)", color: "white", height: "30px", display: "flex", justifyContent: "center", alignItems: "center", fontSize: 12, cursor: 'pointer', border: selectedPerformanceFilter === 'late16_30' ? `2px solid black` : 'none' }}
                  >
                    {performanceStats.late16_30 || 0}
                  </div>
                  <div className="text-center"
                    onClick={() => setSelectedPerformanceFilter('late31_60')}
                    style={{ background: "linear-gradient(to bottom left, #d97706, #b45309)", color: "white", height: "30px", display: "flex", justifyContent: "center", alignItems: "center", fontSize: 12, cursor: 'pointer', border: selectedPerformanceFilter === 'late31_60' ? `2px solid black` : 'none' }}
                  >
                    {performanceStats.late31_60 || 0}
                  </div>
                  <div className="text-center"
                    onClick={() => setSelectedPerformanceFilter('late61_90')}
                    style={{ background: "linear-gradient(to bottom left, #bf6d00, #9a5a00)", color: "white", height: "30px", display: "flex", justifyContent: "center", alignItems: "center", fontSize: 12, cursor: 'pointer', border: selectedPerformanceFilter === 'late61_90' ? `2px solid ${isDarkMode ? 'white' : 'black'}` : 'none' }}
                  >
                    {performanceStats.late61_90 || 0}
                  </div>
                  <div className="text-center"
                    onClick={() => setSelectedPerformanceFilter('gte90')}
                    style={{ background: "linear-gradient(to bottom left, #d66d75, #c94f5c)", color: "white", height: "30px", display: "flex", justifyContent: "center", alignItems: "center", fontSize: 12, cursor: 'pointer', border: selectedPerformanceFilter === 'gte90' ? `2px solid ${isDarkMode ? 'white' : 'black'}` : 'none' }}
                  >
                    {performanceStats.gte90 || 0}
                  </div>
                  <div className="text-center"
                    onClick={() => setSelectedPerformanceFilter('gte120')}
                    style={{ background: "linear-gradient(to bottom left, #ef5350, #e53935)", color: "white", height: "30px", display: "flex", justifyContent: "center", alignItems: "center", fontSize: 12, cursor: 'pointer', border: selectedPerformanceFilter === 'gte120' ? `2px solid ${isDarkMode ? 'white' : 'black'}` : 'none' }}
                  >
                    {performanceStats.gte120 || 0}
                  </div>
                  <div className="text-center"
                    onClick={() => setSelectedPerformanceFilter('extd')}
                    style={{ background: isDarkMode ? "#27293d" : "#ffffff", color: isDarkMode ? "white" : "#333", border: selectedPerformanceFilter === 'extd' ? `2px solid ${activeColorStyle.borderColor}` : (isDarkMode ? '1px solid rgba(255,255,255,0.1)' : "1px solid rgba(0,0,0,0.1)"), height: "30px", display: "flex", justifyContent: "center", alignItems: "center", fontSize: 12, cursor: 'pointer' }}
                  >
                    {performanceStats.extd || 0}
                  </div>
                </div>
              </div>

              {/* More Details Toggle */}
              {data?.payments?.length > 0 && (
                <div
                  className="cursor-pointer text-center mb-2"
                  onClick={() => setIsPerformanceDetailsOpen(!isPerformanceDetailsOpen)}
                  style={{
                    fontSize: isTheme2
                      ? "var(--sidebar-shell-theme-2-nav-font-size, 14px)"
                      : "14px",
                    cursor: "pointer",
                    padding: "5px",
                    borderTop: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #eee",
                    borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #eee",
                    fontWeight: "bold",
                    color: "#1d8cf8",
                  }}
                >
                  <i className={isPerformanceDetailsOpen ? "tim-icons icon-minimal-up" : "tim-icons icon-minimal-down"} style={{ marginRight: '5px' }} />
                  More Details
                  <i className={isPerformanceDetailsOpen ? "tim-icons icon-minimal-up" : "tim-icons icon-minimal-down"} style={{ marginLeft: '5px' }} />
                </div>
              )}

              <Collapse isOpen={isPerformanceDetailsOpen}>
                {/* Payment List Table */}
                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                  <table className="table table-sm" style={{ borderCollapse: 'separate', borderSpacing: '0 2px' }}>
                    <thead className="text-primary">
                      <tr>
                        <th style={{ position: 'sticky', top: 0, background: isDarkMode ? '#27293d' : 'white', zIndex: 1, color: isDarkMode ? 'white' : undefined }}>Type</th>
                        <th style={{ position: 'sticky', top: 0, background: isDarkMode ? '#27293d' : 'white', zIndex: 1, color: isDarkMode ? 'white' : undefined }}>Amt</th>
                        <th style={{ position: 'sticky', top: 0, background: isDarkMode ? '#27293d' : 'white', zIndex: 1, color: isDarkMode ? 'white' : undefined }}>Due On</th>
                        <th style={{ position: 'sticky', top: 0, background: isDarkMode ? '#27293d' : 'white', zIndex: 1, color: isDarkMode ? 'white' : undefined }}>Posted On</th>
                        <th style={{ position: 'sticky', top: 0, background: isDarkMode ? '#27293d' : 'white', zIndex: 1, color: isDarkMode ? 'white' : undefined }}>Days Late</th>
                        <th style={{ position: 'sticky', top: 0, background: isDarkMode ? '#27293d' : 'white', zIndex: 1, color: isDarkMode ? 'white' : undefined }}>History Tier</th>
                        <th style={{ position: 'sticky', top: 0, background: isDarkMode ? '#27293d' : 'white', zIndex: 1, width: 28 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayments.map((payment, idx) => {
                        let rowStyle = {};
                        const lateDay = payment.late_day || 0;
                        if (lateDay <= 0) rowStyle = { background: '#2ecc71', color: '#ffffff' };
                        else if (lateDay <= 15) rowStyle = { background: '#f6ad55', color: '#ffffff' };
                        else if (lateDay <= 30) rowStyle = { background: '#f6a04d', color: '#ffffff' };
                        else if (lateDay <= 60) rowStyle = { background: '#d97706', color: '#ffffff' };
                        else if (lateDay <= 90) rowStyle = { background: '#bf6d00', color: '#ffffff' };
                        else if (lateDay < 120) rowStyle = { background: '#d66d75', color: '#ffffff' };
                        else rowStyle = { background: '#ef5350', color: '#ffffff' };

                        const rowId = payment.id || payment.documentId || idx;
                        const isExpanded = !!expandedPerformanceRows[rowId];
                        const detailItems = Array.isArray(payment.detail) ? payment.detail : [];
                        const dueOnDate = formatDateForDisplay(
                          detailItems[0]?.date ||
                            detailItems[0]?.due_date ||
                            detailItems[0]?.due_on ||
                            payment.due_date ||
                            payment.due_on ||
                            payment.date
                        );

                        return (
                          <React.Fragment key={rowId}>
                            <tr
                              style={{ ...rowStyle, cursor: detailItems.length ? "pointer" : "default" }}
                              onClick={() => {
                                if (detailItems.length) togglePerformanceExpand(rowId);
                              }}
                            >
                              <td style={{ color: 'inherit' }}>{payment.payment_form || '-'}</td>
                              <td style={{ color: 'inherit' }}>{formatPriceWithDollar(payment.amount || 0)}</td>
                              <td style={{ color: 'inherit' }}>{dueOnDate}</td>
                              <td style={{ color: 'inherit' }}>{formatDateForDisplay(payment.createdAt || payment.date)}</td>
                              <td style={{ color: 'inherit' }}>{lateDay}</td>
                              <td style={{ color: 'inherit' }}>{payment.history_tier ?? 0}</td>
                              <td style={{ color: 'inherit', textAlign: 'center', width: 28 }}>
                                {detailItems.length > 0 && (
                                  <i
                                    className={isExpanded ? "tim-icons icon-minimal-up" : "tim-icons icon-minimal-down"}
                                    style={{ fontSize: 10 }}
                                  />
                                )}
                              </td>
                            </tr>
                            {isExpanded && detailItems.length > 0 && (
                              <tr>
                                <td
                                  colSpan={7}
                                  className="p-2"
                                  style={{
                                    backgroundColor: isDarkMode ? "#1e1e2f" : "#f8f9fa",
                                    color: isDarkMode ? "#fff" : "#333",
                                  }}
                                >
                                  <table className="table table-sm mb-0" style={{ fontSize: "12px" }}>
                                    <thead>
                                      <tr>
                                        <th style={{ borderTop: 0 }}>Collected</th>
                                        <th style={{ borderTop: 0 }}>Comments/Ref</th>
                                        <th style={{ borderTop: 0 }}>Date</th>
                                        <th style={{ borderTop: 0 }}>Receipt #</th>
                                        <th style={{ borderTop: 0 }}>Pmt Method</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {detailItems.map((item, detailIdx) => {
                                        const collected = item.payment_applied ?? item.amount ?? item.collected ?? 0;
                                        return (
                                          <tr key={item.id || detailIdx}>
                                            <td>({formatPriceWithDollar(collected)})</td>
                                            <td>
                                              {item.note ||
                                                item.comments ||
                                                item.ref_number ||
                                                payment.note ||
                                                payment.ref_number ||
                                                payment.comments ||
                                                "-"}
                                            </td>
                                            <td>
                                              {formatDateTimeForDisplay(
                                                payment.createdAt || payment.date
                                              )}
                                            </td>
                                            <td>
                                              {item.receipt_id ||
                                                payment.receipt_id ||
                                                payment.receiptId ||
                                                "-"}
                                            </td>
                                            <td>
                                              {item.payment_form ||
                                                item.delivery_method ||
                                                payment.payment_form ||
                                                payment.delivery_method ||
                                                "-"}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Collapse>
              {(!data?.payments || data.payments.length === 0) && (
                <div className="text-center text-muted py-3">{t('snapshotTab.noPerformanceData')}</div>
              )}
            </CardBody>
          </Card>

          {showTransactionHistory && (
          <Card className="mt-2">
            <CardHeader>
              <CardTitle className="mb-0" style={{ fontWeight: "bold" }}>{t('snapshotTab.transactionHistory')}</CardTitle>
            </CardHeader>
            <CardBody className="pt-2" style={{ maxHeight: 400, overflowY: "auto" }}>
              {transactionRows.length === 0 ? (
                <div className="text-center text-muted py-5">{t('snapshotTab.noTransactions')}</div>
              ) : (
                <div style={{ width: "100%", overflowX: "auto" }}>
                  <table
                    className="table table-sm mb-0"
                    style={{
                      borderCollapse: "separate",
                      borderSpacing: 0,
                      width: "100%",
                      tableLayout: "fixed",
                      border: ".5px solid gray",
                    }}
                  >
                    <thead>
                      <tr>
                        {[
                          { content: t("transactions.postedBy"), width: "11%" },
                          {
                            content: (
                              <StackedHeader
                                lines={[t("transactions.postDate"), t("transactions.effectDate")]}
                              />
                            ),
                            width: "16%",
                          },
                          {
                            content: (
                              <StackedHeader
                                lines={[t("transactions.begAcct"), t("transactions.balance")]}
                              />
                            ),
                            width: "11%",
                          },
                          {
                            content: (
                              <StackedHeader
                                lines={[t("transactions.amount"), t("transactions.collected")]}
                              />
                            ),
                            width: "10%",
                          },
                          {
                            content: (
                              <StackedHeader
                                lines={[t("transactions.adjusted"), t("transactions.amount")]}
                              />
                            ),
                            width: "10%",
                          },
                          {
                            content: (
                              <StackedHeader
                                lines={[t("transactions.endAcct"), t("transactions.balance")]}
                              />
                            ),
                            width: "11%",
                          },
                          {
                            content: (
                              <StackedHeader
                                lines={[t("transactions.payment"), t("transactions.method")]}
                              />
                            ),
                            width: "12%",
                          },
                          {
                            content: (
                              <StackedHeader
                                lines={[t("transactions.comments"), t("transactions.ref")]}
                              />
                            ),
                            width: "19%",
                          },
                        ].map((header, idx) => (
                          <th
                            key={idx}
                            style={{
                              backgroundColor: activeColorStyle.borderColor,
                              color: "#fff",
                              fontWeight: 600,
                              padding: "8px 4px",
                              position: "sticky",
                              textTransform: "math-auto",
                              top: 0,
                              zIndex: 1,
                              lineHeight: 1.15,
                              fontSize: 11.7,
                              width: header.width,
                              whiteSpace: "normal",
                              verticalAlign: "bottom",
                              overflow: "hidden",
                            }}
                          >
                            {header.content}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {transactionRows.slice(0, 5).map((row) => (
                        <React.Fragment key={row.id}>
                          <tr
                            onClick={() => toggleExpand(row.id)}
                            style={{ cursor: "pointer" }}
                          >
                            <td
                              style={{
                                color: txExpandedStyles.rowText,
                                textAlign: "center",
                                fontWeight: 500,
                                fontSize: 11.7,
                                padding: "6px 4px",
                                wordBreak: "break-word",
                                overflow: "hidden",
                              }}
                            >
                              {row.posted_by || "-"}
                            </td>
                            <td
                              style={{
                                color: txExpandedStyles.rowText,
                                textAlign: "center",
                                fontSize: 11.7,
                                padding: "6px 2px",
                                overflow: "hidden",
                              }}
                            >
                              <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {formatCompactDateTime(row.date)}
                              </div>
                              <div
                                style={{
                                  fontSize: 11.7,
                                  color: txExpandedStyles.rowSubText,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {formatCompactDateTime(row.effect_date)}
                              </div>
                            </td>
                            <td
                              style={{
                                color: txExpandedStyles.rowText,
                                textAlign: "center",
                                fontSize: 11.7,
                                padding: "6px 2px",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {formatPriceWithDollar(row.beginningBalance)}
                            </td>
                            <td
                              style={{
                                color: txExpandedStyles.collectedCell,
                                textAlign: "center",
                                fontSize: 11.7,
                                fontWeight: "bold",
                                padding: "6px 2px",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {(() => {
                                const val = row.amount;
                                const formatted = formatPriceWithDollar(val);
                                const isReversal = row.payment_type === "reversal" || row.payment_type === "def_down_reversal";
                                if (isReversal) return formatted;
                                return val < 0
                                  ? `(${formatted.replace("$", "").replace(",", "")})`
                                  : `(${formatted})`;
                              })()}
                            </td>
                            <td
                              style={{
                                color: txExpandedStyles.rowText,
                                textAlign: "center",
                                fontSize: 11.7,
                                padding: "6px 2px",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {(() => {
                                const n = Number(row.adjusted) || 0;
                                if (Math.abs(n) < 0.009) return formatPriceWithDollar(0);
                                if (row.payment_type === "payoff" && n > 0.009) {
                                  return `(${formatPriceWithDollar(n)})`;
                                }
                                if (n < -0.009) {
                                  return `(${formatPriceWithDollar(Math.abs(n))})`;
                                }
                                return formatPriceWithDollar(n);
                              })()}
                            </td>
                            <td
                              style={{
                                color: txExpandedStyles.rowText,
                                textAlign: "center",
                                fontSize: 11.7,
                                padding: "6px 2px",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {formatPriceWithDollar(row.endingBalance)}
                            </td>
                            <td
                              style={{
                                color: txExpandedStyles.rowText,
                                textAlign: "center",
                                fontSize: 11.7,
                                padding: "6px 4px",
                                wordBreak: "break-word",
                                overflow: "hidden",
                              }}
                            >
                              {row.paymentForm && row.paymentForm !== "-" ? row.paymentForm : "-"}
                            </td>
                            <td
                              style={{
                                color: txExpandedStyles.rowText,
                                fontSize: 11.7,
                                padding: "6px 6px",
                                verticalAlign: "middle",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 6,
                                  minWidth: 0,
                                }}
                              >
                                <div style={{ textAlign: "left", minWidth: 0, flex: 1 }}>
                                  <div
                                    style={{
                                      whiteSpace: "normal",
                                      overflowWrap: "anywhere",
                                      wordBreak: "break-word",
                                      lineHeight: 1.25,
                                    }}
                                  >
                                    {row.note && row.note !== "-" ? row.note : ""}
                                  </div>
                                  <div
                                    style={{
                                      color: txExpandedStyles.rowSubText,
                                      whiteSpace: "normal",
                                      overflowWrap: "anywhere",
                                      wordBreak: "break-word",
                                      lineHeight: 1.25,
                                    }}
                                  >
                                    {row.refNumber && row.refNumber !== "-" ? row.refNumber : ""}
                                  </div>
                                </div>
                                <i
                                  className={
                                    expandedRows[row.id]
                                      ? "tim-icons icon-minimal-up"
                                      : "tim-icons icon-minimal-down"
                                  }
                                  style={{
                                    fontSize: 10,
                                    flexShrink: 0,
                                    color: txExpandedStyles.rowText,
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                          {expandedRows[row.id] && renderExpandedContent(row)}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
          )}
        </Col>
        <Col md="6">
          <Card className="mb-2 pb-2">
            <CardHeader>
              <CardTitle className="mb-0" style={{ fontWeight: "bold" }}>{t('snapshotTab.notesSummary')}</CardTitle>
            </CardHeader>
            <CardBody className="pt-2">
              {(() => {
                const buckets = [
                  { id: "calls_out", label: t("snapshotTab.callsOut"), value: notesBuckets.callsOut || 0 },
                  { id: "calls_in", label: t("snapshotTab.callsIn"), value: notesBuckets.callsIn || 0 },
                  { id: "notes", label: t("snapshotTab.collector"), value: notesBuckets.collector || 0 },
                  { id: "main", label: t("snapshotTab.system"), value: notesBuckets.system || 0 },
                  { id: "ip", label: t("snapshotTab.ip"), value: notesBuckets.ip || 0 },
                  { id: "ptp", label: t("snapshotTab.ptp"), value: notesBuckets.ptp || 0 },
                ];
                const toggleBucket = (id) => {
                  setSelectedNoteBucket((prev) => (prev === id ? null : id));
                };
                return (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 0 }}>
                      {buckets.map((bucket) => (
                        <button
                          key={bucket.id}
                          type="button"
                          onClick={() => toggleBucket(bucket.id)}
                          className="text-center"
                          style={{
                            backgroundColor: activeColorStyle.borderColor,
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 600,
                            padding: "8px 0",
                            border: "transparent",
                            outline: "none",
                            cursor: "pointer",
                          }}
                        >
                          {bucket.label}
                        </button>
                      ))}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 0, marginTop: 0 }}>
                      {buckets.map((bucket) => (
                        <button
                          key={bucket.id}
                          type="button"
                          onClick={() => toggleBucket(bucket.id)}
                          className="text-center"
                          style={{
                            background: isDarkMode ? "rgba(255,255,255,0.04)" : "#f3f5f9",
                            color: isDarkMode ? "#fff" : "#333",
                            padding: "7px 0",
                            fontSize: 12,
                            fontWeight: 600,
                            border: `2px solid ${selectedNoteBucket === bucket.id ? activeColorStyle.borderColor : "transparent"}`,
                            outline: "none",
                            cursor: "pointer",
                          }}
                        >
                          {bucket.value}
                        </button>
                      ))}
                    </div>
                  </>
                );
              })()}
            </CardBody>
          </Card>
          <NotesTab
            module="accounts"
            id={accountId || data?.documentId}
            activeColor={activeColor}
            hideActionsSection={true}
            hidePageSizeControl={true}
            defaultPageSize={8}
            isTheme2={isTheme2}
            filterKey={selectedNoteBucket || undefined}
          />
        </Col>
      </Row>

      <ReceiptDrawer
        isOpen={isReceiptOpen}
        onClose={() => setIsReceiptOpen(false)}
        transaction={selectedTransaction}
        accountData={data}
      />
    </div>
  );
};

export default SnapshotTab;
